/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import Form from "../../ui/Form/Form";
import CopyButton from "../buttons/CopyButton";
import TagList from "../tagList/TagList";
import SearchableMultiSelect from "../searchableMultiSelect/SearchableMultiSelect";
import {
  FORM_VALIDATION_TRANSLATIONS,
  SIDEPANEL_GENERAL_TRANSLATIONS,
} from "../../translations/util";
import ToggleField from "../inputs/ToggleField";
import NumberField from "../inputs/NumberField";
import DropdownField from "../inputs/DropdownField";

interface IProps {
  esRef: string;
  data: any;
  setData: any;
  isEditing: boolean;
  channelIds: { id: string; text: string }[];
  selectedItems: any;
  setSelected: any;
  formFields: any;
}

const EditDFSIGroupRowFormFields = ({
  esRef,
  data,
  setData,
  isEditing,
  channelIds,
  selectedItems,
  setSelected,
  formFields,
}: IProps) => {
  const handleSelection = (item: any) => {
    let newArray = [...selectedItems];
    if (newArray.find((obj) => obj.id === item.id)) {
      newArray = newArray.filter((obj) => obj.id !== item.id);
    } else {
      newArray.push(item);
    }
    setSelected([...newArray]);
    setData({
      ...data,
      "CHANNEL-IDS": newArray,
    });
  };
  return (
    <Form>
      <div className="readonly-data" data-cy="readonly-section">
        <ul>
          <li className="flex items-center" data-cy="referenceID">
            <h5>
              {SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields["Reference ID"]}
            </h5>
            <div className="data-content">
              <span
                className={(data.Ref || esRef).length > 20 ? "truncate" : ""}
              >
                {data.Ref || esRef}
              </span>
              <CopyButton
                copyContent={data.Ref || esRef}
                copyField="referenceID"
              />
            </div>
          </li>

          <li className="flex items-center" data-cy="creationDate">
            {data.CreationDate ? (
              <div className="flex items-center mr-3">
                <h5 data-cy="creationDate-heading">
                  {
                    SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields
                      .CreationDate
                  }
                </h5>
                <div className="data-content" data-cy="creationDate-data">
                  <span> {data.CreationDate} </span>
                </div>
              </div>
            ) : null}

            {data.ModificationDate && (
              <div className="flex items-center" data-cy="modificationDate">
                <h5 data-cy="modificationDate-heading">
                  {
                    SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields
                      .ModificationDate
                  }
                </h5>
                <div className="data-content" data-cy="modificationDate-data">
                  <span> {data.ModificationDate} </span>
                </div>
              </div>
            )}
          </li>

          {data["DFSI-GROUP-NAME"] ? (
            <li className="flex items-center " data-cy="dfsiGroupName">
              <h5>
                {
                  SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields[
                    "DFSI-GROUP-NAME"
                  ]
                }
              </h5>
              <div className="data-content">
                <span> {data["DFSI-GROUP-NAME"]} </span>
                <CopyButton copyContent={data["DFSI-GROUP-NAME"]} />
              </div>
            </li>
          ) : null}

          {data["DFSI-LOGICAL-GID"] ? (
            <li className="flex items-center " data-cy="dfsiLogicalGID">
              <h5>
                {
                  SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields[
                    "DFSI-LOGICAL-GID"
                  ]
                }
              </h5>
              <div className="data-content">
                <span> {data["DFSI-LOGICAL-GID"]} </span>
                <CopyButton copyContent={data["DFSI-LOGICAL-GID"]} />
              </div>
            </li>
          ) : null}

          {data["IWF-ID"] ? (
            <li className="flex items-center " data-cy="IWF_ID">
              <h5>
                {SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields["IWF-ID"]}
              </h5>
              <div className="data-content">
                <span> {data["IWF-ID"]} </span>
                <CopyButton copyContent={data["IWF-ID"]} />
              </div>
            </li>
          ) : null}
        </ul>
      </div>

      {formFields.map(
        (rows: {
          cols: number;
          fields: {
            name: string;
            tooltipText?: string;
            label: string;
            type: string;
            invalidText?: string | null;
            isInputInvalid?: (val?: string | number) => boolean;
            min?: number;
            max?: number;
            items?: string[];
          }[];
        }) => {
          return (
            <div className={`grid grid-cols-${rows.cols} gap-4 form-control`}>
              {rows.fields.map((field) => {
                switch (field.type) {
                  case "SearchableMultiSelect":
                    return (
                      <div className="grid">
                        <SearchableMultiSelect
                          titleText={field.label}
                          placeholder={field.label}
                          items={channelIds}
                          tooltipText={field.tooltipText}
                          selectedItems={selectedItems}
                          handleSelection={handleSelection}
                          disabled={!isEditing}
                          required
                          clearSelected={() => {
                            setSelected([]);
                            setData({
                              ...data,
                              "CHANNEL-IDS": [],
                            });
                          }}
                        />
                        <TagList
                          tagListArray={selectedItems?.map(
                            (obj: any) => obj?.text ?? obj["CHANNEL-ID"]
                          )}
                          disabled={!isEditing}
                        />
                      </div>
                    );
                  case "TOGGLE":
                    return (
                      <ToggleField
                        labelText={field.label}
                        onChange={(val) =>
                          setData({
                            ...data,
                            [field.name]: val,
                          })
                        }
                        value={data[field.name] as boolean}
                        tooltipText={field.tooltipText}
                        defaultToggled
                        disabled={!isEditing}
                      />
                    );
                  case "NUMBER":
                    return (
                      <NumberField
                        onChange={(val) => {
                          if (!val || !Number.isNaN(+val))
                            setData({
                              ...data,
                              [field.name]: val ? +val : undefined,
                            });
                        }}
                        label={field.label}
                        placeholder={field.label}
                        max={field.max || 0}
                        min={field.min || 0}
                        disabled={!(isEditing && data["ENCRYPTION-ENABLED"])}
                        invalid={
                          !(isEditing && data["ENCRYPTION-ENABLED"])
                            ? false
                            : data[field.name] !== 0 && !data[field.name]
                        }
                        value={data[field.name] as number}
                        invalidText={
                          field.invalidText ||
                          `"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`
                        }
                        tooltipText={field.tooltipText}
                        required
                      />
                    );
                  default:
                    return (
                      <DropdownField
                        className="dropdown-field"
                        label={field.label}
                        selectedItem={data[field.name]}
                        items={field.items || []}
                        onChange={({
                          selectedItem,
                        }: {
                          selectedItem: string;
                        }) =>
                          setData({
                            ...data,
                            [field.name]: selectedItem,
                          })
                        }
                        tooltipText={field.tooltipText}
                        required
                        disabled={!isEditing}
                      />
                    );
                }
              })}
            </div>
          );
        }
      )}
    </Form>
  );
};

export default EditDFSIGroupRowFormFields;
